// Knows whether any form on screen is mid-edit, without any form having to know
// the PWA layer exists. Module-level so it is readable outside every React
// provider, same reason as `pendingWorkSnapshot`.
//
// Only opaque registration ids are ever stored — never field names, never field
// values. Form content in this app is LGPD-sensitive (CPF, salary), so keeping
// it out of this layer keeps it out of the update policy and out of Sentry.

import type { FormDirtiness } from "./updateApplyPolicy";

type Listener = () => void;

const dirtyForms = new Set<string>();
const listeners = new Set<Listener>();

function notify() {
	for (const fn of listeners) fn();
}

export function markFormDirty(formId: string): void {
	if (dirtyForms.has(formId)) return;
	dirtyForms.add(formId);
	notify();
}

/**
 * Also the unregister path: a form calls this from its effect cleanup, so the
 * entry cannot outlive the form that owns it. The set is bounded by mounted
 * forms, never by session length.
 */
export function markFormClean(formId: string): void {
	if (!dirtyForms.delete(formId)) return;
	notify();
}

/**
 * Never returns `unknown`: this registry is synchronous and authoritative — a
 * form that has not registered cannot be dirty. `unknown` exists on the shared
 * tri-state for `pendingWork`, whose snapshot is read asynchronously.
 */
export function getFormDirtiness(): FormDirtiness {
	return dirtyForms.size > 0 ? "dirty" : "clean";
}

export function subscribeDirtyForms(listener: Listener): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

// Exposed for unit tests only — allows resetting module state between test cases.
export function _resetForTesting() {
	dirtyForms.clear();
	listeners.clear();
}
